import {
  JupiterTheme,
  MockHook,
  NAVBAR_HEIGHT,
  CurrentOEMBrandingLogo,
} from '@jupiterone/jupiter-web-toolkit';
import { createStyles, makeStyles } from '@material-ui/styles';
import React from 'react';
import useJupiterAppBarLogic, {
  JupiterAppBarComponentProps,
} from './useJupiterAppBarLogic';
import Fade from '@material-ui/core/Fade';
import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import AccountMenu from './AccountMenu';
import AppsMenu from './AppsMenu';
import SettingsMenu from 'navbar/src/jupiterAppBar/SettingsMenu';
import TrialAlert from 'navbar/src/jupiterAppBar/TrialAlert';
import ResourcesMenu from 'navbar/src/jupiterAppBar/ResourcesMenu';
import Notifications from 'navbar/src/jupiterAppBar/Notifications';
import clsx from 'clsx';
import SearchAnywhereContainer from './SearchAnywhereContainer';
import SearchAnywhereIcon from './SearchAnywhereIcon';
import { ClickAwayListener } from '@material-ui/core';
import { Link } from 'react-router-dom';

const useStyles = makeStyles((theme: JupiterTheme) => {
  const { spacing } = theme;

  return createStyles({
    appBar: {
      background: theme.palette.background.dark,
      position: 'relative',
      width: '100%',
      zIndex: theme.zIndex.drawer + 2,
    },
    rootCollapsed: {
      background: theme.palette.background.dark,
      width: 'inherit',
      position: 'fixed',
      top: spacing(1.25),
      right: spacing(1.25),
      '& $nav svg': {
        '&.active': {
          color: theme.palette.primary.light,
        },
        '&:hover': {
          color: theme.palette.primary.light,
        },
      },
    },
    nav: {
      alignItems: 'center',
      display: 'flex',
      color: theme.palette.grey[50],
    },
    head: {
      padding: `6px 0`,
      minHeight: NAVBAR_HEIGHT,
    },
    homeLink: {
      background: 'transparent',
      display: 'flex',
      alignItems: 'center',
      textDecoration: 'none',
    },
    menuPaper: {
      borderTop: `solid 1px ${theme.palette.grey[500]}`,
      border: 'none',
      borderRadius: 0,
      display: 'flex',
      flexDirection: 'column',
    },
    popCollapsed: {
      '& $menuPaper': {
        borderTop: 'none',
        marginTop: 64,
        right: 10,
      },
    },
    listMenu: {
      marginTop: theme.spacing(5.5),
      marginRight: 0,
      marginLeft: -theme.spacing(3),
      display: 'flex',
      flexDirection: 'column',
    },

    navCenter: {
      alignItems: 'center',
      color: theme.palette.grey[500],
      display: 'flex',
      flex: '1 0 auto',
    },

    hidden: {
      display: 'none',
    },
  });
});

export const JupiterAppBarComponent = ({
  collapsed,
  defaultAppPath,
  featureEntitlementState,
  notificationsEnabled,
  children,
  location,
  searchAnywhereOpen,
  hasAccessToLanding,
  handleSearchAnywhereToggle,
  handleSearchAnywhereDisable,
  searchAnywhereEnabled,
}: JupiterAppBarComponentProps) => {
  const classes = useStyles();
  const menuProps = {
    TransitionComponent: Fade,
  };
  return (
    <AppBar
      position="static"
      className={clsx(
        collapsed ? classes.rootCollapsed : classes.appBar,
        location.pathname.includes('/onboarding') && classes.hidden
      )}
    >
      <Toolbar className={classes.head}>
        <div className={classes.navCenter}>
          {!collapsed && (
            <Link
              aria-label="Home"
              to={defaultAppPath}
              className={classes.homeLink}
            >
              <CurrentOEMBrandingLogo />
            </Link>
          )}
          {featureEntitlementState.name &&
            featureEntitlementState.trialEndDate && (
              <TrialAlert trialEndDate={featureEntitlementState.trialEndDate} />
            )}
        </div>

        {children}

        <nav className={classes.nav}>
          {searchAnywhereEnabled && !location.pathname.includes('/landing') && (
            <ClickAwayListener
              onClickAway={() =>
                searchAnywhereOpen && handleSearchAnywhereDisable()
              }
            >
              <div>
                {hasAccessToLanding && (
                  <>
                    <SearchAnywhereIcon
                      searchAnywhereOpen={searchAnywhereOpen}
                      handleSearchAnywhereToggle={handleSearchAnywhereToggle}
                    />
                    <SearchAnywhereContainer
                      searchAnywhereOpen={searchAnywhereOpen}
                      handleSearchAnywhereDisable={handleSearchAnywhereDisable}
                    />
                  </>
                )}
              </div>
            </ClickAwayListener>
          )}
          <AppsMenu />
          <SettingsMenu
            className={collapsed ? classes.popCollapsed : classes.listMenu}
            PopoverClasses={{ paper: classes.menuPaper }}
            {...menuProps}
          />
          {notificationsEnabled && <Notifications />}
          <ResourcesMenu
            className={collapsed ? classes.popCollapsed : classes.listMenu}
            PopoverClasses={{ paper: classes.menuPaper }}
            {...menuProps}
          />
          <AccountMenu collapsed={collapsed} />
        </nav>
      </Toolbar>
    </AppBar>
  );
};

export default MockHook.withHookHoc(
  JupiterAppBarComponent,
  useJupiterAppBarLogic
);
